import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { Router } from "@angular/router";
import { CurrentUserResponse, Provider, UserSettings } from "@models/*";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { MAX_USER_SETTINGS_SIZE_MB } from "@shared/constants/allowed-upload-file-types.constants";
import {
    UploadSizeCheckResult,
    isUploadSizeExceeded,
} from "@shared/helpers/upload-size-check.helper";
import { AdminApiService } from "app/api/admin.api.service";
import { AuthApiService } from "app/api/auth.api.service";
import { getFirebaseErrorKey } from "app/auth/helpers/auth.helper";
import { InternalLoginPreferenceService } from "app/auth/internal-login-preference.service";
import { FirebaseProfileUser } from "app/core/models/firebase-profile-user.interface";
import { TenantConfig } from "app/core/models/tenant-config.interface";
import { Tenant } from "app/core/models/tenant.interface";
import { QRCodeService } from "app/core/services/qrcode.service";
import {
    EmailAuthProvider,
    User as FirebaseUser,
    MultiFactorAssertion,
    MultiFactorResolver,
    OAuthProvider,
    TotpMultiFactorGenerator,
    browserLocalPersistence,
    getMultiFactorResolver,
    multiFactor,
    onAuthStateChanged,
    reauthenticateWithCredential,
    signInWithEmailAndPassword,
    signInWithPopup,
} from "firebase/auth";
import { MessageService } from "primeng/api";
import {
    EMPTY,
    Observable,
    catchError,
    combineLatest,
    filter,
    from,
    map,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from "rxjs";
import * as tenantActions from "../../../core/state/tenant/actions/tenant.actions";
import * as fromTenant from "../../../core/state/tenant/reducers";
import { authActions } from "../actions/auth.actions";
import * as fromAuth from "../reducers";

export const effectLogging: boolean = false;

@Injectable()
export class AuthEffects {
    private readonly firebaseAuthState$ = new Observable<FirebaseUser | null>(
        (subscriber) =>
            onAuthStateChanged(
                this.auth,
                (user) => subscriber.next(user),
                (error) => subscriber.error(error),
            ),
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    maxUserSettingsSize = MAX_USER_SETTINGS_SIZE_MB;

    constructor(
        private actions$: Actions,
        private api: AuthApiService,
        private adminApi: AdminApiService,
        private store: Store,
        private router: Router,
        private internalLoginPreference: InternalLoginPreferenceService,
        private qrCodeService: QRCodeService,
        private messageService: MessageService,
        private translateService: TranslateService,
        private auth: Auth,
    ) {}

    logAllAuthActions$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(...Object.values(authActions)),
                tap((action) => {
                    if (effectLogging) {
                    }
                }),
            ),
        { dispatch: false },
    );

    loadAuthProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.loadAuthProviders),
            switchMap(({ tenantKey }) =>
                this.api.getAuthProviders({ tenantKey }).pipe(
                    tap(() => {
                        this.store.dispatch(
                            authActions.setTenantKey({ tenantKey }),
                        );
                        this.router.navigate(["login", "credentials"]);
                    }),
                    map((response) =>
                        authActions.loadAuthProvidersSuccess({ response }),
                    ),
                    catchError((error) =>
                        of(authActions.loadAuthProvidersError({ error })),
                    ),
                ),
            ),
        ),
    );

    loadInternalAuthProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.loadInternalAuthProviders),
            switchMap(({ providerPayload }) =>
                this.api.getAuthProviders(providerPayload).pipe(
                    tap(() => {
                        this.store.dispatch(
                            authActions.setTenantKey({
                                tenantKey: providerPayload.tenantKey,
                            }),
                        );
                    }),
                    map((response) =>
                        authActions.loadInternalAuthProvidersSuccess({
                            response,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            authActions.loadInternalAuthProvidersError({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.login),
            switchMap(({ payload }) => {
                this.auth.tenantId = payload.tenantId;
                this.auth.setPersistence(browserLocalPersistence);

                return from(
                    signInWithEmailAndPassword(
                        this.auth,
                        payload.email,
                        payload.password,
                    ),
                ).pipe(
                    map((userCredential) => {
                        return authActions.loginSuccess({
                            isTotpEnabled: false,
                            password: payload.password,
                        });
                    }),
                    catchError((error) => {
                        const mfaPendingCredential =
                            error.customData?._serverResponse
                                ?.mfaPendingCredential;
                        const mfaInfo =
                            error.customData?._serverResponse?.mfaInfo;

                        if (
                            error.code === "auth/multi-factor-auth-required" &&
                            mfaPendingCredential &&
                            mfaInfo
                        ) {
                            try {
                                const resolver: MultiFactorResolver =
                                    getMultiFactorResolver(this.auth, error);
                                return of(
                                    authActions.promptForTotpVerification({
                                        resolver,
                                    }),
                                );
                            } catch (e) {
                                return of(
                                    authActions.loginError({
                                        error: "FIREBASE.ERROR.MFA_RESOLUTION_FAILED.TEXT",
                                    }),
                                );
                            }
                        }

                        const errorKey = getFirebaseErrorKey(error);
                        return of(authActions.loginError({ error: errorKey }));
                    }),
                );
            }),
        ),
    );

    loginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.loginSuccess),
            switchMap(() => [
                authActions.getCurrentUser(),
                tenantActions.getTenantInfo(),
                tenantActions.getTenantConfig(),
                authActions.getFirebaseUser(),
            ]),
        ),
    );

    loginWithProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.loginWithProvider),
            switchMap(
                ({
                    provider,
                    tenantId,
                }: {
                    provider: Provider;
                    tenantId: string;
                }) => {
                    this.auth.tenantId = tenantId || null;

                    const oauthProvider = new OAuthProvider(
                        provider.providerId,
                    );

                    return from(signInWithPopup(this.auth, oauthProvider)).pipe(
                        map(() =>
                            authActions.loginSuccess({
                                isTotpEnabled: false,
                                password: "",
                            }),
                        ),
                        catchError((error) => {
                            const mfaPendingCredential =
                                error.customData?._serverResponse
                                    ?.mfaPendingCredential;
                            const mfaInfo =
                                error.customData?._serverResponse?.mfaInfo;

                            if (
                                error.code ===
                                    "auth/multi-factor-auth-required" &&
                                mfaPendingCredential &&
                                mfaInfo
                            ) {
                                try {
                                    const resolver: MultiFactorResolver =
                                        getMultiFactorResolver(
                                            this.auth,
                                            error,
                                        );
                                    return of(
                                        authActions.promptForTotpVerification({
                                            resolver,
                                        }),
                                    );
                                } catch {
                                    return of(
                                        authActions.loginWithProviderError({
                                            error: "FIREBASE.ERROR.MFA_RESOLUTION_FAILED.TEXT",
                                        }),
                                    );
                                }
                            }

                            const errorKey = getFirebaseErrorKey(error);
                            return of(
                                authActions.loginWithProviderError({
                                    error: errorKey,
                                }),
                            );
                        }),
                    );
                },
            ),
        ),
    );

    verifyTotpSignIn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.verifyTotpSignIn),
            withLatestFrom(this.store.pipe(select(fromAuth.getMfaResolver))),
            switchMap(
                ([{ otpCode }, resolver]: [
                    { otpCode: string },
                    MultiFactorResolver | null,
                ]) => {
                    if (!resolver) {
                        return of(
                            authActions.verifyTotpSignInFailure({
                                error: "FIREBASE.ERROR.NO_MFA_RESOLVER.TEXT",
                            }),
                        );
                    }

                    const totpHint = resolver.hints.find(
                        (hint) =>
                            hint.factorId ===
                            TotpMultiFactorGenerator.FACTOR_ID,
                    );
                    if (!totpHint) {
                        return of(
                            authActions.verifyTotpSignInFailure({
                                error: "FIREBASE.ERROR.NO_TOTP_FACTOR.TEXT",
                            }),
                        );
                    }

                    try {
                        const totpAssertion =
                            TotpMultiFactorGenerator.assertionForSignIn(
                                totpHint.uid,
                                otpCode,
                            );

                        return from(resolver.resolveSignIn(totpAssertion)).pipe(
                            map(() => {
                                return authActions.loginSuccess({
                                    isTotpEnabled: true,
                                    password: null,
                                });
                            }),
                            catchError((error) => {
                                return of(
                                    authActions.verifyTotpSignInFailure({
                                        error: getFirebaseErrorKey(error),
                                    }),
                                );
                            }),
                        );
                    } catch (error: any) {
                        return of(
                            authActions.verifyTotpSignInFailure({
                                error: "FIREBASE.ERROR.UNEXPECTED_TOTP_SIGNIN_ERROR.TEXT",
                            }),
                        );
                    }
                },
            ),
        ),
    );

    getCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getCurrentUser),
            tap(() => this.store.dispatch(authActions.logoutFromBackend())),
            switchMap(() =>
                this.api.getCurrentUser().pipe(
                    map((response) =>
                        authActions.getCurrentUserSuccess({ response }),
                    ),
                    catchError((error) =>
                        of(authActions.getCurrentUserError({ error })),
                    ),
                ),
            ),
        ),
    );

    getFirebaseUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getFirebaseUser),
            switchMap(() =>
                this.firebaseAuthState$.pipe(
                    take(1),
                    switchMap((user) => {
                        if (user) {
                            return of(
                                authActions.getFirebaseUserSuccess({
                                    firebaseUser: user as FirebaseProfileUser,
                                }),
                            );
                        }

                        return of(
                            authActions.getFirebaseUserFailure({
                                error: new HttpErrorResponse({
                                    error: "No user found",
                                    status: 404,
                                }),
                            }),
                        );
                    }),
                    catchError((error) =>
                        of(
                            authActions.getFirebaseUserFailure({
                                error:
                                    error instanceof HttpErrorResponse
                                        ? error
                                        : new HttpErrorResponse({
                                              error,
                                              status: 500,
                                          }),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    enforceTotpOnRefresh$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.enforce2FAonReload),
                switchMap(() =>
                    this.store
                        .select(fromAuth.getFirebaseUser)
                        .pipe(filter(Boolean), take(1)),
                ),
                withLatestFrom(this.store.select(fromAuth.getIsPasswordLogin)),
                filter(([_, isPasswordLogin]) => isPasswordLogin),
                switchMap(() => {
                    const tenantConfig$ = this.store
                        .select(fromTenant.getTenantConfig)
                        .pipe(filter(Boolean), take(1));
                    const firebaseUser$ = this.store
                        .select(fromAuth.getFirebaseUser)
                        .pipe(filter(Boolean), take(1));

                    return combineLatest([tenantConfig$, firebaseUser$]).pipe(
                        tap(([tenantConfig, firebaseUser]) => {
                            const enforce = tenantConfig.enforce2FA;
                            const emailVerified = firebaseUser.emailVerified;
                            const isTotpEnabled =
                                multiFactor(firebaseUser).enrolledFactors
                                    .length > 0;

                            if (!enforce) return;

                            if (!emailVerified || !isTotpEnabled) {
                                this.store.dispatch(
                                    authActions.logoutForEnforcing2FA(),
                                );
                            }
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    enforceTotpIfRequired$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.loginSuccess),
                switchMap(({ password, isTotpEnabled }) => {
                    const tenantConfig$ = this.store
                        .select(fromTenant.getTenantConfig)
                        .pipe(
                            filter(
                                (config): config is TenantConfig => !!config,
                            ),
                            take(1),
                        );

                    const firebaseUser$ = this.store
                        .select(fromAuth.getFirebaseUser)
                        .pipe(
                            filter(
                                (user): user is FirebaseProfileUser => !!user,
                            ),
                            take(1),
                        );

                    return combineLatest([tenantConfig$, firebaseUser$]).pipe(
                        take(1),
                        tap(([tenantConfig, firebaseUser]) => {
                            const enforce = tenantConfig.enforce2FA;

                            const providerIds = firebaseUser.providerData.map(
                                (p) => p.providerId,
                            );
                            const isPasswordLogin =
                                providerIds.includes("password");

                            if (!isPasswordLogin) {
                                this.router.navigate(["/app"]);
                                return;
                            }

                            if (enforce) {
                                if (!firebaseUser.emailVerified) {
                                    this.store.dispatch(
                                        authActions.promptEmailVerification(),
                                    );
                                    return;
                                }

                                if (!isTotpEnabled) {
                                    this.store.dispatch(
                                        authActions.promptToSetupTotp({
                                            password,
                                        }),
                                    );
                                    return;
                                }
                            }

                            this.router.navigate(["/app"]);
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    reauthenticateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.reauthenticateUser),
            switchMap(({ credentials, enable2FA }) =>
                combineLatest([
                    this.store.select(fromTenant.getTenantInfo).pipe(
                        filter((t): t is Tenant => !!t),
                        take(1),
                    ),
                    this.store.select(fromAuth.getUser).pipe(
                        filter((u): u is CurrentUserResponse => !!u),
                        take(1),
                    ),
                ]).pipe(
                    switchMap(([tenant, currentUser]) => {
                        this.auth.tenantId = tenant.firebaseTenantId || "";

                        const credential = EmailAuthProvider.credential(
                            currentUser.email!,
                            credentials.password,
                        );

                        return from(
                            reauthenticateWithCredential(
                                this.auth.currentUser!,
                                credential,
                            ),
                        ).pipe(
                            map(() =>
                                authActions.reauthenticateSuccess({
                                    credentials,
                                    enable2FA,
                                }),
                            ),
                            catchError((error) => {
                                if (
                                    error.code ===
                                    "auth/multi-factor-auth-required"
                                ) {
                                    try {
                                        const resolver = getMultiFactorResolver(
                                            this.auth,
                                            error,
                                        );
                                        const totpHint = resolver.hints.find(
                                            (hint) =>
                                                hint.factorId ===
                                                TotpMultiFactorGenerator.FACTOR_ID,
                                        );

                                        if (!totpHint) {
                                            return of(
                                                authActions.reauthenticateFailure(
                                                    {
                                                        error: "FIREBASE.ERROR.NO_TOTP_FACTOR.TEXT",
                                                    },
                                                ),
                                            );
                                        }

                                        if (!credentials.totpCode) {
                                            return of(
                                                authActions.reauthenticateFailure(
                                                    {
                                                        error: "FIREBASE.ERROR.TOTP_CODE_REQUIRED.TEXT",
                                                    },
                                                ),
                                            );
                                        }

                                        const totpAssertion =
                                            TotpMultiFactorGenerator.assertionForSignIn(
                                                totpHint.uid,
                                                credentials.totpCode,
                                            );

                                        return from(
                                            resolver.resolveSignIn(
                                                totpAssertion,
                                            ),
                                        ).pipe(
                                            map(() =>
                                                authActions.reauthenticateSuccess(
                                                    {
                                                        credentials,
                                                        enable2FA,
                                                    },
                                                ),
                                            ),
                                            catchError((err) =>
                                                of(
                                                    authActions.reauthenticateFailure(
                                                        {
                                                            error: getFirebaseErrorKey(
                                                                err,
                                                            ),
                                                        },
                                                    ),
                                                ),
                                            ),
                                        );
                                    } catch {
                                        return of(
                                            authActions.reauthenticateFailure({
                                                error: "FIREBASE.ERROR.MFA_RESOLUTION_FAILED.TEXT",
                                            }),
                                        );
                                    }
                                }

                                return of(
                                    authActions.reauthenticateFailure({
                                        error: getFirebaseErrorKey(error),
                                    }),
                                );
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    enableOrDisableTotpAfterReauth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.reauthenticateSuccess),
            map(({ credentials, enable2FA }) =>
                enable2FA
                    ? authActions.startTotpEnrollment()
                    : authActions.disableTotpMfa(),
            ),
        ),
    );

    startTotpEnrollment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.startTotpEnrollment),
            withLatestFrom(this.store.select(fromAuth.getUser)),
            switchMap(([_, user]) => {
                if (!user)
                    return of(
                        authActions.startTotpEnrollmentFailure({
                            error: "FIREBASE.ERROR.USER_NOT_FOUND.TEXT",
                        }),
                    );

                if (!this.auth.currentUser)
                    return of(
                        authActions.startTotpEnrollmentFailure({
                            error: "FIREBASE.ERROR.USER_NOT_AUTHENTICATED.TEXT",
                        }),
                    );

                return from(
                    multiFactor(this.auth.currentUser).getSession(),
                ).pipe(
                    switchMap((session) =>
                        from(
                            TotpMultiFactorGenerator.generateSecret(session),
                        ).pipe(
                            map((totpSecret) => {
                                const secretKey =
                                    (totpSecret as any).secretKey ||
                                    (totpSecret as any).secret ||
                                    "";
                                return authActions.startTotpEnrollmentSuccess({
                                    totpSecret,
                                    secretKey,
                                });
                            }),
                            catchError((error) =>
                                of(
                                    authActions.startTotpEnrollmentFailure({
                                        error: getFirebaseErrorKey(error),
                                    }),
                                ),
                            ),
                        ),
                    ),
                    catchError((error) =>
                        of(
                            authActions.startTotpEnrollmentFailure({
                                error: getFirebaseErrorKey(error),
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    generateTotpQrCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.startTotpEnrollmentSuccess),
            switchMap(({ secretKey }) =>
                from(this.qrCodeService.generateQRCode(secretKey)).pipe(
                    map((qrCodeUrl) =>
                        authActions.generateTotpQrCodeSuccess({ qrCodeUrl }),
                    ),
                    catchError((error) =>
                        of(
                            authActions.generateTotpQrCodeFailure({
                                error: getFirebaseErrorKey(error),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    verifyTotpEnrollment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.verifyTotpEnrollment),
            withLatestFrom(
                this.store.select(fromAuth.getUser),
                this.store.select(fromAuth.getTotpSecret),
            ),
            switchMap(([{ otpCode }, user, totpSecret]) => {
                if (!user || !totpSecret)
                    return of(
                        authActions.verifyTotpEnrollmentFailure({
                            error: "FIREBASE.ERROR.USER_OR_SECRET_MISSING.TEXT",
                        }),
                    );

                if (!this.auth.currentUser)
                    return of(
                        authActions.verifyTotpEnrollmentFailure({
                            error: "FIREBASE.ERROR.USER_NOT_AUTHENTICATED.TEXT",
                        }),
                    );

                try {
                    const assertion =
                        TotpMultiFactorGenerator.assertionForEnrollment(
                            totpSecret,
                            otpCode,
                        );
                    return from(
                        multiFactor(this.auth.currentUser).enroll(
                            assertion as MultiFactorAssertion,
                            "Authenticator App",
                        ),
                    ).pipe(
                        map(() => authActions.verifyTotpEnrollmentSuccess()),
                        catchError((error) =>
                            of(
                                authActions.verifyTotpEnrollmentFailure({
                                    error: getFirebaseErrorKey(error),
                                }),
                            ),
                        ),
                    );
                } catch (error) {
                    return of(
                        authActions.verifyTotpEnrollmentFailure({
                            error: "FIREBASE.ERROR.UNEXPECTED_TOTP_ENROLLMENT_ERROR.TEXT",
                        }),
                    );
                }
            }),
        ),
    );

    disableTotpMfa$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.disableTotpMfa),
            withLatestFrom(this.store.select(fromAuth.getUser)),
            switchMap(([_, user]) => {
                if (!user)
                    return of(
                        authActions.disableTotpMfaFailure({
                            error: "FIREBASE.ERROR.USER_NOT_FOUND.TEXT",
                        }),
                    );

                if (!this.auth.currentUser)
                    return of(
                        authActions.disableTotpMfaFailure({
                            error: "FIREBASE.ERROR.USER_NOT_AUTHENTICATED.TEXT",
                        }),
                    );

                const multiFactorUser = multiFactor(this.auth.currentUser);
                if (!multiFactorUser || !multiFactorUser.enrolledFactors.length)
                    return of(
                        authActions.disableTotpMfaFailure({
                            error: "FIREBASE.ERROR.NO_ENROLLED_FACTORS.TEXT",
                        }),
                    );

                const totpFactor = multiFactorUser.enrolledFactors.find(
                    (f) => f.factorId === "totp",
                );
                if (!totpFactor)
                    return of(
                        authActions.disableTotpMfaFailure({
                            error: "FIREBASE.ERROR.NO_TOTP_FACTOR.TEXT",
                        }),
                    );

                return from(multiFactorUser.unenroll(totpFactor.uid)).pipe(
                    map(() => authActions.disableTotpMfaSuccess()),
                    catchError((error) =>
                        of(
                            authActions.disableTotpMfaFailure({
                                error: getFirebaseErrorKey(error),
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    refreshUserAfterTotpChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                authActions.disableTotpMfaSuccess,
                authActions.verifyTotpEnrollmentSuccess,
            ),
            map(() => authActions.getFirebaseUser()),
        ),
    );

    showEnableTotpSuccessToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.verifyTotpEnrollmentSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: "success",
                        summary: this.translateService.instant(
                            "APP_SETTINGS.ENABLE_2FA_SUCCESS_TITLE",
                        ),
                        detail: this.translateService.instant(
                            "APP_SETTINGS.ENABLE_2FA_SUCCESS_MESSAGE",
                        ),
                    });
                }),
            ),
        { dispatch: false },
    );

    showDisableTotpSuccessToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.disableTotpMfaSuccess),
                tap(() => {
                    this.messageService.add({
                        severity: "success",
                        summary: this.translateService.instant(
                            "APP_SETTINGS.DISABLE_2FA_SUCCESS_TITLE",
                        ),
                        detail: this.translateService.instant(
                            "APP_SETTINGS.DISABLE_2FA_SUCCESS_MESSAGE",
                        ),
                    });
                }),
            ),
        { dispatch: false },
    );

    // Might fuck with the state
    authStateChanges$ = createEffect(() =>
        this.firebaseAuthState$.pipe(
            withLatestFrom(this.store.select(fromAuth.getAuthInitialized)),
            filter(([_, initialized]) => !initialized),
            switchMap(([user]) => {
                const firebaseUser = user as FirebaseProfileUser | null;
                const hasTotp = firebaseUser
                    ? multiFactor(firebaseUser).enrolledFactors.length > 0
                    : false;

                return of(
                    firebaseUser
                        ? authActions.getFirebaseUserSuccess({ firebaseUser })
                        : authActions.getFirebaseUserFailure({ error: null }),

                    authActions.setLoggedIn({ payload: !!firebaseUser }),

                    authActions.setTotpEnabled({ enabled: hasTotp }),
                );
            }),
        ),
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.logout),
            switchMap(() => {
                return of(authActions.logoutFromBackend()).pipe(
                    tap(() => {
                        const anchors =
                            document.querySelectorAll('a[href^="blob:"]');
                        anchors.forEach((anchor) => {
                            const href = anchor.getAttribute("href");
                            if (href?.startsWith("blob:")) {
                                window.URL.revokeObjectURL(href);
                            }
                        });
                    }),
                    switchMap(() => {
                        return from(this.auth.signOut()).pipe(
                            tap(() =>
                                this.router.navigate(
                                    this.internalLoginPreference.loginRoute,
                                    {
                                        replaceUrl: true,
                                    },
                                ),
                            ),
                            map(() => authActions.logoutSuccess()),
                        );
                    }),
                );
            }),
        ),
    );

    logoutFromBackend$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.logoutFromBackend),
                switchMap(() =>
                    this.api.logout().pipe(catchError(() => of(null))),
                ),
            ),
        { dispatch: false },
    );

    logoutForEnforcing2FA$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.logoutForEnforcing2FA),
            switchMap(() => {
                return from(this.auth.signOut()).pipe(
                    tap(() =>
                        this.router.navigate(["/login"], { replaceUrl: true }),
                    ),
                    map(() => authActions.logoutForEnforcing2FASuccess()),
                );
            }),
        ),
    );

    verifyEmail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.verifyEmail),
            switchMap(({}) =>
                this.api.verifyEmail().pipe(
                    map(() => authActions.verifyEmailSuccess()),
                    catchError((error) =>
                        of(authActions.verifyEmailError({ error })),
                    ),
                ),
            ),
        ),
    );

    resetPassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.resetPassword),
            switchMap(({ payload }) =>
                this.api.resetPassword(payload).pipe(
                    map(() => authActions.resetPasswordSuccess()),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.ACTION.CHANGE_PASSWORD.SUCCESS.TITLE",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(authActions.resetPasswordError({ error })),
                    ),
                ),
            ),
        ),
    );

    updateCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveCurrentUser),
            switchMap(({ user }) =>
                this.api.saveCurrentUser(user).pipe(
                    map((res) => authActions.saveCurrentUserSuccess({ res })),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.HEADER.SETTINGS_DROPDOWN.ACTIONS.UPDATE_USER.SUMMARY",
                            ),
                        });
                    }),
                ),
            ),
        ),
    );

    updateCurrentUserPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.updateCurrentUserPhoto),
            switchMap(({ photo }) =>
                this.api.uploadPhoto(photo).pipe(
                    map((response) =>
                        authActions.updateCurrentUserPhotoSuccess({ response }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.HEADER.SETTINGS_DROPDOWN.ACTIONS.UPDATE_USER_PHOTO.SUMMARY",
                            ),
                        });
                    }),
                ),
            ),
        ),
    );

    deleteCurrentUserPhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.deleteCurrentUserPhoto),
            switchMap(() =>
                this.api.deletePhoto().pipe(
                    map(() => authActions.deleteCurrentUserPhotoSuccess()),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "APP.HEADER.SETTINGS_DROPDOWN.ACTIONS.DELETE_USER_PHOTO.SUMMARY",
                            ),
                        });
                    }),
                ),
            ),
        ),
    );

    setUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.setUserSettings),
            withLatestFrom(this.store.select(fromAuth.getUserSettings)),
            switchMap(([{ settings: partialUpdate }, currentSettings]) => {
                const fullSettings: UserSettings = {
                    ...currentSettings!,
                    ...partialUpdate,
                };
                const checkresult: UploadSizeCheckResult = isUploadSizeExceeded(
                    fullSettings,
                    "settings",
                );

                if (checkresult.exceeded) {
                    console.warn(
                        `UserSettings upload-size exceeded. Max allowed size is ${checkresult.maxSizeMb} MB. Settings not saved!`,
                    );
                    return EMPTY;
                }

                return this.api.setSettings(fullSettings).pipe(
                    map((updatedSettings) =>
                        authActions.setUserSettingsSuccess({
                            res: updatedSettings,
                        }),
                    ),
                );
            }),
        ),
    );

    getOidcProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getOidcProvider),
            switchMap(() =>
                this.adminApi.getOidcProviders().pipe(
                    map((response) =>
                        authActions.getOidcProvidersSuccess({ response }),
                    ),
                    catchError((error) =>
                        of(authActions.loginError({ error })),
                    ),
                ),
            ),
        ),
    );

    createOidcProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.createOidcProvider),
            switchMap(({ provider }) =>
                this.adminApi.createOidcProvider(provider).pipe(
                    map((response) =>
                        authActions.createOidcProviderSuccess({ response }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "ADMIN.TENANT_SETTINGS.OIDC.CREATE_SUCCESS",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(authActions.createOidcProviderFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    saveOidcProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveOidcProvider),
            switchMap(({ providerId, provider }) =>
                this.adminApi.saveOidcProvider(providerId, provider).pipe(
                    map((response) =>
                        authActions.saveOidcProviderSuccess({ response }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "ADMIN.TENANT_SETTINGS.OIDC.SAVE_SUCCESS",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(authActions.saveOidcProviderFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    deleteOidcProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.deleteOidcProvider),
            switchMap(({ providerId }) =>
                this.adminApi.deleteOidcProvider(providerId).pipe(
                    map(() =>
                        authActions.deleteOidcProviderSuccess({ providerId }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "ADMIN.TENANT_SETTINGS.OIDC.DELETE_SUCCESS",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(authActions.deleteOidcProviderFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    setOidcProviderEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.setOidcProviderEnabled),
            switchMap(({ provider }) =>
                this.adminApi.setOidcProviderEnabled(provider).pipe(
                    map((response) =>
                        authActions.setOidcProviderEnabledSuccess({ response }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "ADMIN.TENANT_SETTINGS.OIDC.SAVE_SUCCESS",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(
                            authActions.setOidcProviderEnabledError({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    getOidcCallbackURL$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getOidcCallbackURL),
            switchMap(() =>
                this.adminApi.getOidcCallbackURL().pipe(
                    map((response) =>
                        authActions.getOidcCallbackURLSuccess({
                            url: response,
                        }),
                    ),
                    catchError((error) =>
                        of(authActions.getOidcCallbackURLError({ error })),
                    ),
                ),
            ),
        ),
    );

    getEmailPasswordEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.getEmailPasswordEnabled),
            switchMap(() =>
                this.adminApi.getEmailPasswordEnabled().pipe(
                    map((response) =>
                        authActions.getEmailPasswordEnabledSuccess({
                            enabled: response.enabled,
                        }),
                    ),
                    catchError((error) =>
                        of(authActions.loginError({ error })),
                    ),
                ),
            ),
        ),
    );

    setEmailPasswordEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.setEmailPasswordEnabled),
            switchMap(({ enabled }) =>
                this.adminApi.setEmailPasswordEnabled(enabled).pipe(
                    map((response) =>
                        authActions.setEmailPasswordEnabledSuccess({
                            enabled: response.enabled,
                        }),
                    ),
                    tap(() => {
                        this.messageService.add({
                            severity: "success",
                            summary:
                                this.translateService.instant(
                                    "APP.ACTION.SUCCESS",
                                ),
                            detail: this.translateService.instant(
                                "ADMIN.TENANT_SETTINGS.EMAIL_PASSWORD.SAVE_SUCCESS",
                            ),
                        });
                    }),
                    catchError((error) =>
                        of(authActions.loginError({ error })),
                    ),
                ),
            ),
        ),
    );

    saveSelectedDashboardId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveSelectedDashboardId),
            withLatestFrom(this.store.select(fromAuth.getUser)),
            switchMap(([{ dashboardId }, user]) => {
                const fullSettings: UserSettings = {
                    ...user?.settings,
                    selectedDashboardId: dashboardId,
                };
                return this.api.setSettings(fullSettings).pipe(
                    map((updatedSettings) =>
                        authActions.setUserSettingsSuccess({
                            res: updatedSettings,
                        }),
                    ),
                );
            }),
        ),
    );

    saveSelectedDashboardTimeframe$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveSelectedDashboardTimeframe),
            withLatestFrom(this.store.select(fromAuth.getUser)),
            switchMap(([{ timeframe }, user]) => {
                const fullSettings: UserSettings = {
                    ...user?.settings,
                    selectedDashboardTimeframe: timeframe,
                };
                return this.api.setSettings(fullSettings).pipe(
                    map((updatedSettings) =>
                        authActions.setUserSettingsSuccess({
                            res: updatedSettings,
                        }),
                    ),
                );
            }),
        ),
    );

    saveIncidentsListViewMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.saveIncidentsListViewMode),
            withLatestFrom(this.store.select(fromAuth.getUser)),
            switchMap(([{ viewMode }, user]) => {
                const fullSettings: UserSettings = {
                    ...user?.settings,
                    incidentsListViewMode: viewMode,
                };
                return this.api.setSettings(fullSettings).pipe(
                    map((updatedSettings) =>
                        authActions.setUserSettingsSuccess({
                            res: updatedSettings,
                        }),
                    ),
                );
            }),
        ),
    );
}
